import Select from 'react-select';
import EventBus from 'eventing-bus';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';

import { formatProperties } from '../../utils/propertyFormatter';
import { bulkUpdateObjects, getProperties } from '../../store/Objects';

const BulkEditModal = ({ isOpen, onClose, objectSchema, selectedIds, onRefresh, onClearSelection }) => {
  const dispatch = useDispatch();
  const { members } = useSelector(state => state['Settings']);
  
  // Get object schema from constants
  const objectType = objectSchema['type'];
  const objectData = useSelector(state => state['Objects']?.[objectType]);
  const properties = objectData?.['properties'] || {};

  const [updates, setUpdates] = useState({});
  const [propertyValue, setPropertyValue] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);

  useEffect(() => { if (isOpen) dispatch(getProperties()); }, [isOpen, objectType]);

  // Get all properties for the current objectType
  const allModuleProperties = properties || {};
  
  // Flatten all properties from different groups
  const getAllProperties = () => {
    const flatProperties = [];
    Object.values(allModuleProperties || {}).forEach(group => { if (Array.isArray(group)) flatProperties.push(...group); });
    return formatProperties(flatProperties, objectType);
  };

  // Filter out readOnly properties and system groups
  const getEditableProperties = () => {
    const allProps = getAllProperties() || [];
    if (!allProps.length) return [];

    const excludedGroups = ['System Information', 'Associated Records'];
    
    return allProps.filter(property => {
      // Check if property is readOnly
      if (property['displayConfig']?.['readOnly'] === true) return false;
      // Check if property is in excluded groups
      if (excludedGroups.includes(property['group'])) return false;
      // Check if property should be shown in forms
      if (property['displayConfig']?.['showInForm'] === false) return false;
      
      // Include property if it passes all checks
      return true;
    });
  };

  const editableProperties = getEditableProperties();

  // Group properties by their group for the dropdown
  const getPropertyOptions = () => {
    const grouped = editableProperties.reduce((acc, property) => {
      const group = property['group'] || 'General';
      if (!acc[group]) acc[group] = [];
      acc[group].push({ value: property['_id'], label: property['label'], property });
      return acc;
    }, {});

    // Convert to react-select format
    return Object.entries(grouped).map(([group, options]) => ({ label: group, options }));
  };

  const handlePropertySelect = (option) => {
    if (option) {
      const property = editableProperties.find(p => p['_id'] === option['value']);
      setSelectedProperty(property);
      setPropertyValue(updates[property?.['name']] || '');
    } else {
      setSelectedProperty(null);
      setPropertyValue('');
    }
  };

  const renderPropertyInput = () => {
    if (!selectedProperty) return null;
    
    const { type, options } = selectedProperty;
    switch (type) {
      case 'text':
      case 'string':
        return (
          <input
            type="text"
            className="form-control"
            value={propertyValue}
            onChange={(e) => setPropertyValue(e.target.value)}
            placeholder={`Enter ${selectedProperty['label']}`}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            className="form-control"
            value={propertyValue}
            onChange={(e) => setPropertyValue(e.target.value)}
            placeholder={`Enter ${selectedProperty['label']}`}
          />
        );

      case 'select':
        const selectOptions = options?.map(opt => ({ value: opt['value'] || opt, label: opt['label'] || opt })) || [];

        return (
          <Select
            value={selectOptions.find(opt => opt['value'] === propertyValue)}
            onChange={(option) => setPropertyValue(option?.['value'] || '')}
            options={selectOptions}
            isClearable
            placeholder={`Select ${selectedProperty['label']}`}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        );

      case 'date':
        return (
          <input
            type="date"
            className="form-control"
            value={propertyValue}
            onChange={(e) => setPropertyValue(e.target.value)}
          />
        );

      case 'datetime':
        // Helper functions to handle datetime parsing
        const getDateFromDateTime = (isoString) => {
          if (!isoString) return '';
          const date = new Date(isoString);
          return date.toISOString().split('T')[0];
        };

        const getTimeFromDateTime = (isoString) => {
          if (!isoString) return '';
          const date = new Date(isoString);
          return date.toTimeString().split(' ')[0].substring(0, 5); // HH:MM format
        };

        const combineDateTimeForBulk = (dateValue, timeValue) => {
          if (!dateValue) return '';
          const combinedValue = timeValue ? `${dateValue}T${timeValue}:00` : `${dateValue}T00:00:00`;
          return new Date(combinedValue).toISOString();
        };

        const currentDateValue = getDateFromDateTime(propertyValue);
        const currentTimeValue = getTimeFromDateTime(propertyValue);

        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="date"
              className="form-control"
              value={currentDateValue}
              onChange={(e) => {
                const newDateValue = e.target.value;
                const newDateTime = combineDateTimeForBulk(newDateValue, currentTimeValue);
                setPropertyValue(newDateTime);
              }}
              style={{ flex: '1', minWidth: '140px' }}
            />
            <input
              type="time"
              className="form-control"
              value={currentTimeValue}
              onChange={(e) => {
                const newTimeValue = e.target.value;
                const newDateTime = combineDateTimeForBulk(currentDateValue, newTimeValue);
                setPropertyValue(newDateTime);
              }}
              style={{ flex: '1', minWidth: '100px' }}
            />
          </div>
        );

      case 'boolean':
        return (
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="booleanCheck"
              checked={propertyValue === true}
              onChange={(e) => setPropertyValue(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="booleanCheck">
              {selectedProperty['label']}
            </label>
          </div>
        );

      case 'user':
        const userOptions = members?.map(user => ({ value: user['_id'], label: user['name'] || user['email'] })) || [];

        return (
          <Select
            value={userOptions.find(opt => opt['value'] === propertyValue)}
            onChange={(option) => setPropertyValue(option?.['value'] || '')}
            options={userOptions}
            isClearable
            placeholder="Select user"
            className="react-select-container"
            classNamePrefix="react-select"
          />
        );

      default:
        return (
          <input
            type="text"
            className="form-control"
            value={propertyValue}
            onChange={(e) => setPropertyValue(e.target.value)}
            placeholder={`Enter ${selectedProperty['label']}`}
          />
        );
    }
  };

  const handleAddUpdate = () => {
    if (selectedProperty && propertyValue !== '') {
      setUpdates(prev => ({ ...prev, [selectedProperty['name']]: propertyValue }));
      setSelectedProperty(null);
      setPropertyValue('');
    }
  };

  const handleRemoveUpdate = (propertyName) => {
    setUpdates(prev => {
      const newUpdates = { ...prev };
      delete newUpdates[propertyName];
      return newUpdates;
    });
  }

  const handleBulkUpdate = () => {
    if (Object.keys(updates).length > 0) {
      // Separate custom fields from system fields
      const systemFields = {};
      const customFields = {};
      
      Object.entries(updates).forEach(([propertyName, value]) => {
        const property = editableProperties.find(p => p['name'] === propertyName);
        if (property && property['isSystem'] === false) {
          customFields[propertyName] = value;
        } else {
          systemFields[propertyName] = value;
        }
      });
      
      // Prepare the final updates object
      const finalUpdates = { ...systemFields };
      if (Object.keys(customFields).length > 0) {
        finalUpdates.customFields = customFields;
      }
      
      dispatch(bulkUpdateObjects({
        updates: finalUpdates,
        ids: selectedIds,
        objectType: objectSchema['type'],
        successCallback: () => {
          // Only clear selection on successful update
          setUpdates({});
          setPropertyValue('');
          setSelectedProperty(null);
          onClose();
          onClearSelection();
          if (onRefresh) onRefresh();
        }
      }));
    } else EventBus.publish("error", `Please select at least one property to update`);
  };

  const handleClose = () => {
    // Reset form state but don't clear selection on cancel
    onClose();
    setUpdates({});
    setPropertyValue('');
    setSelectedProperty(null);
    // Commented out: onClearSelection() - Don't clear selection on cancel, only on success
    // onClearSelection();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} keyboard={true} className="main-modal modal-dialog-centered new-user-modal bulkedit-modal">
      <ModalHeader toggle={handleClose}></ModalHeader>
      <ModalBody>
        <div className="content-area">
           <h4>Bulk edit {selectedIds.length} {selectedIds.length > 1 ? objectSchema['plural'] : objectSchema['label']}</h4>
          <div className="form-area">
            <div className="form-group">
              <label className="form-label">Property to update</label>
              <Select
              // menuIsOpen={true}
                value={selectedProperty ? { value: selectedProperty['_id'], label: selectedProperty['label'] } : null}
                onChange={handlePropertySelect}
                options={getPropertyOptions()}
                placeholder="Select a property to edit"
                isClearable
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({ ...base, backgroundColor: '#fff', borderColor: '#EFF4F9', minHeight: '48px', '&:hover': { borderColor: '#EFF4F9' } }),
                  menu: (base) => ({ ...base, backgroundColor: '#fff', border: '1px solid #EFF4F9' }),
                  option: (base, state) => ({ ...base, backgroundColor: state['isFocused'] ? '#EFF4F9' : '#fff', color: '#fff', '&:active': { backgroundColor: '#fff' } }),
                  singleValue: (base) => ({ ...base, color: '#4B5563' }),
                  placeholder: (base) => ({ ...base, color: '#9ca3af' }),
                  input: (base) => ({ ...base, color: '#4B5563' }),
                  groupHeading: (base) => ({ ...base, color: '#4B5563', fontSize: '12px', textTransform: 'uppercase', fontWeight: '600', paddingLeft: '12px', paddingRight: '12px' })
                }}
              />
            </div>

            {selectedProperty && (
              <div className="form-group mt-4">
                <label className="form-label">{selectedProperty['label']}</label>
                {renderPropertyInput()}
                <button
                  className="btn-style-new btn-sm mt-3"
                  onClick={handleAddUpdate}
                  disabled={propertyValue === ''}
                >
                  Add to updates
                </button>
              </div>
            )}

            {Object.keys(updates).length > 0 && (
              <div className="mt-4">
                <h6 className="mb-3">Pending Updates:</h6>
                <div className="update-list">
                  {Object.entries(updates).map(([propertyName, value]) => {
                    const property = editableProperties.find(p => p['name'] === propertyName);
                    return (
                      <div key={propertyName} className="update-item d-flex justify-content-between align-items-center mb-2 p-3">
                        <div>
                          <strong>{property?.['label'] || propertyName}:</strong>{' '}
                          <span className="text-muted">
                            {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                          </span>
                        </div>
                        <button
                          className="cross-btn"
                          onClick={() => handleRemoveUpdate(propertyName)}
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="btns-box text-right mt-4 ">
              <button className="btn-style-border mr-3" onClick={handleClose}>
                Cancel
              </button>
              <button
                className="btn-style-new"
                onClick={handleBulkUpdate}
                disabled={Object.keys(updates).length === 0}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default BulkEditModal;